import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, X } from "lucide-react";
import { notifyPing } from "@/lib/notify";

export const ChatWidget = ({ businessId, businessName }) => {
  const { lang } = useLang();
  const storeKey = `urbis_chat_${businessId}`;
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null); // { session_id, name }
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [starting, setStarting] = useState(false);
  const [unread, setUnread] = useState(0);
  const endRef = useRef(null);
  const openRef = useRef(false);
  const prevBizCount = useRef(null);
  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storeKey);
      if (saved) setSession(JSON.parse(saved));
    } catch (e) { /* ignore */ }
  }, [storeKey]);

  const loadMsgs = useCallback(() => {
    if (!session?.session_id) return;
    api.get(`/chat/${session.session_id}/messages`)
      .then((r) => {
        const messages = r.data.messages || [];
        setMsgs(messages);
        const bizCount = messages.filter((m) => m.sender === "business").length;
        if (prevBizCount.current !== null && bizCount > prevBizCount.current && !openRef.current) { notifyPing(); toast.message(lang === "es" ? `Nuevo mensaje de ${businessName}` : `New message from ${businessName}`); }
        prevBizCount.current = bizCount;
        const cu = r.data.conversation?.client_unread || 0;
        if (openRef.current) {
          setUnread(0);
          if (cu > 0) api.post(`/chat/${session.session_id}/client-read`).catch(() => {});
        } else {
          setUnread(cu);
        }
      })
      .catch((err) => {
        if (err.response?.status === 404) { localStorage.removeItem(storeKey); setSession(null); }
      });
  }, [session, storeKey]);

  useEffect(() => {
    if (!session?.session_id) return;
    loadMsgs();
    const iv = setInterval(loadMsgs, 5000);
    return () => clearInterval(iv);
  }, [session, loadMsgs]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const start = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setStarting(true);
    try {
      const r = await api.post("/chat/start", { business_id: businessId, name: name.trim(), email: email.trim(), city: city.trim() });
      const s = { session_id: r.data.session_id, name: name.trim() };
      localStorage.setItem(storeKey, JSON.stringify(s));
      setSession(s);
    } catch (err) { /* ignore */ }
    finally { setStarting(false); }
  };

  const send = async () => {
    if (!text.trim() || !session?.session_id) return;
    const t2 = text; setText("");
    try {
      await api.post(`/chat/${session.session_id}/client-message`, { text: t2 });
      loadMsgs();
    } catch (err) {
      setText(t2);
      toast.error(lang === "es" ? "No se pudo enviar. Intenta de nuevo." : "Could not send. Try again.");
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50" data-testid="chat-widget">
      {!open && (
        <button
          onClick={() => { setOpen(true); setUnread(0); if (session?.session_id) api.post(`/chat/${session.session_id}/client-read`).catch(() => {}); }}
          data-testid="chat-widget-toggle"
          className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-transform hover:scale-110 hover:bg-orange-600"
          aria-label="Chat"
        >
          <MessageCircle className="h-6 w-6" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-xs font-bold leading-none text-white" data-testid="chat-widget-unread">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}
      {open && (
        <div className="flex h-[460px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl" data-testid="chat-widget-panel">
          <div className="flex items-center justify-between bg-secondary px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-primary" />
              <p className="font-head text-sm font-bold">{businessName}</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close" aria-label="Close"><X className="h-5 w-5" /></button>
          </div>

          {!session ? (
            <form onSubmit={start} className="flex flex-1 flex-col justify-center gap-3 p-5" data-testid="chat-start-form">
              <p className="text-sm text-slate-600">{lang === "es" ? "Inicia una conversación con el negocio." : "Start a conversation with the business."}</p>
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder={lang === "es" ? "Tu nombre" : "Your name"} className="rounded-sm" data-testid="chat-start-name" />
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={lang === "es" ? "Email (opcional)" : "Email (optional)"} className="rounded-sm" data-testid="chat-start-email" />
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder={lang === "es" ? "Ciudad (opcional)" : "City (optional)"} className="rounded-sm" data-testid="chat-start-city" />
              <Button type="submit" disabled={starting} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="chat-start-btn">{starting ? "…" : (lang === "es" ? "Iniciar chat" : "Start chat")}</Button>
            </form>
          ) : (
            <>
              <div className="flex-1 space-y-2 overflow-y-auto bg-slate-50 p-4" data-testid="chat-widget-thread">
                {msgs.length === 0 && <p className="text-center text-xs text-slate-400">{lang === "es" ? "Escribe tu primer mensaje…" : "Write your first message…"}</p>}
                {msgs.map((m, i) => (
                  <div key={m.id || i} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.sender === "client" ? "ml-auto bg-primary text-white" : "bg-white text-slate-800 shadow-sm"}`}>{m.text}</div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="flex gap-2 border-t border-slate-200 p-3">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={lang === "es" ? "Escribe un mensaje…" : "Type a message…"} className="rounded-sm" data-testid="chat-widget-input" />
                <Button onClick={send} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="chat-widget-send"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
